import React, { useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Divider } from '@components/Feed/Divider';
import { FeedError } from '@components/Feed/Placeholders';
import { Spinner } from '@components/basics/Spinner';
import { type ChannelPostsQuery } from '@graphql/reactQuery';
import { useChannelPosts } from '../useChannelPosts';
import { CommentPreviews } from './CommentsPreview';
import { EmptyState } from './Empty';
import { ChannelPost } from './Post';

const PAGE_SIZE = 5;

export type ChannelPost = ChannelPostsQuery['channelPosts']['edges'][number]['node'];

function renderItemContent(item: ChannelPost) {
  return (
    <div className="pb-5 md:pb-0">
      <ChannelPost post={item} />
      <CommentPreviews post={item} />
      <Divider />
    </div>
  );
}

export function ChannelPosts({ channelId }: { channelId: string }) {
  const { posts, loading, loadMore, hasNextPage, isEmpty, error } = useChannelPosts({
    channelId,
    limit: PAGE_SIZE,
  });

  const memoizedFooter = useMemo(
    () => (hasNextPage || loading ? <Spinner className="py-5 text-neutral400" /> : null),
    [hasNextPage, loading],
  );

  return (
    <>
      {!!error && <FeedError mainFeed divider={false} />}
      {isEmpty && <EmptyState />}

      {posts?.length > 0 ? (
        <Virtuoso
          useWindowScroll
          data={posts}
          itemContent={(_, item) => renderItemContent(item)}
          endReached={loadMore}
          components={{ Footer: () => memoizedFooter }}
          style={{ willChange: 'height' }}
          increaseViewportBy={{ bottom: 4000, top: 500 }}
        />
      ) : (
        memoizedFooter
      )}
    </>
  );
}
